/**
 * client/src/admin/Readiness.tsx — is the voice actually able to make audio, and how do we know?
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `/health` SAYS THE SAME WORD FOR A VARIANT THAT WORKS AND ONE THAT DOES NOT.
 *
 * Measured against spark-1 on the morning of 2026-08-08:
 *
 *     GET  /health                                       → {"status":"ok","model_loaded":true,
 *                                                           "variants":["full","turbo"]}
 *     POST /generate {"voice":"karti","variant":"turbo"}  → 200, 1.4 s of 24 kHz WAV
 *     POST /generate {"voice":"karti","variant":"full"}   → 500 Internal Server Error
 *     POST /generate {"voice":"default","variant":"full"} → 500 Internal Server Error
 *
 * `variant: "full"` is §4.11's *"try harder"* escape hatch for a sentence Karti cannot fix any other
 * way — the last thing he reaches for before giving up on a paragraph. It was dead on every book in
 * the library and **nothing in this product could tell him**, because the only signal anyone looked at
 * was a health endpoint that does not exercise the model.
 *
 * **By that afternoon somebody had fixed the box and all four pairs answered 200** — `full` at ~6.2 s
 * against `turbo`'s ~1.4 s for the same 30 characters. Nothing was deployed here. That is the second
 * half of the argument for this screen and not a reason to delete it: a table that only ever shows
 * green is a table nobody trusts, and the day `full` came back is a day this product could otherwise
 * not have distinguished from the day before.
 *
 * So this prints one row per `(voice, variant)` with the **verbatim** failure and the **age** of the
 * answer, so *"we have not been able to check for forty minutes"* and *"we checked and it is broken"*
 * are never the same pixel.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **Age is rendered, always, including on the green rows.** A readiness table with no ages is one
 * that looks identical whether the prober is running or died at boot — which is this repository's most
 * repeated defect (a timer that ships unstarted, 618 lines imported by nothing). `stale` comes from
 * the server, computed against `AB_TTS_READY_PROBE_MIN`, so the client never decides what old means.
 *
 * ⚠️ **`source` is shown and it is not decoration.** `probe` means we generated 30 characters and
 * threw the bytes away. `clip` means a real narration proved the same pair for free — §5.3's *"a
 * successful narration IS the readiness probe"*. `health` means the model was **never asked**, and it
 * can never carry `ok: true`; a green row sourced from `/health` would be the exact lie above.
 *
 * ⚠️ **No new CSS.** Every class here is already in `client/src/ui/app.css`.
 */

import { useCallback, useState, type ReactElement } from "react";

import { ApiError, api } from "../lib/api.ts";

/* ── the wire — `sys/readiness.ts`'s `TtsStatus`, exactly ──────────────────── */

export interface HostReport {
  url: string;
  reachable: boolean;
  latencyMs: number | null;
  modelLoaded: boolean;
  voices: string[];
  variants: string[];
  serves: string[];
  missing: string[];
  unmapped: string[];
  reason: string | null;
  checkedAt: number;
}

export interface ReadinessRow {
  hostUrl: string;
  voiceId: string;
  variant: string;
  ok: boolean;
  source: "probe" | "clip" | "health";
  latencyMs: number | null;
  generateMs: number | null;
  bytes: number | null;
  reason: string | null;
  checkedAt: number;
  ageSec: number;
  stale: boolean;
  line: string;
}

export interface OpsFlag {
  key: string;
  value: string;
  setBy: string | null;
  setAt: number;
  ageSec: number;
}

export interface TtsStatus {
  mode: string;
  voiceId: string;
  narratorOk: boolean;
  inflight: number;
  autoNarrate: string;
  hosts: HostReport[];
  ready: ReadinessRow[];
  probeIntervalMin: number;
  probeDeferred: string | null;
  budget: { spentMs: number; ceilingMs: number; exhausted: boolean; jobs: number } | null;
  listeners: number;
  medianMsPerChar: number | null;
  p95MsPerChar: number | null;
  paceSample: number;
  lanes: { lane: string; served: boolean; drained: boolean }[];
  flags: OpsFlag[];
}

/* ── the screen ───────────────────────────────────────────────────────────── */

export function Readiness({ tts, onChanged }: { tts: TtsStatus | null; onChanged: () => void }): ReactElement {
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);

  const probeNow = useCallback(async () => {
    setBusy(true);
    setSaid(null);
    try {
      const r = await api.post<{ cycle: { ran: boolean; reason: string | null; steps: { outcome: string; ok: boolean | null }[] } }>(
        "/admin/tts/ready",
        {},
      );
      const probed = r.cycle.steps.filter((s) => s.outcome === "probed").length;
      const free = r.cycle.steps.filter((s) => s.outcome === "clip").length;
      const held = r.cycle.steps.filter((s) => s.outcome === "deferred").length;
      // The refusal is reported as loudly as the result. A button that spends no GPU because three
      // people are listening must not look like a button that did nothing.
      setSaid(
        r.cycle.ran
          ? `${probed} generated · ${free} proved free by a real clip · ${held} held back`
          : (r.cycle.reason ?? "nothing to probe"),
      );
      onChanged();
    } catch (err) {
      setSaid(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [onChanged]);

  if (tts === null) return <section className="ab-panel">Asking the voice hosts…</section>;

  const down = tts.ready.filter((r) => !r.ok);

  return (
    <>
      {down.length > 0 ? (
        <section className="ab-panel ab-panel-bad">
          <h2 className="ab-h2">
            {down.length} voice{down.length === 1 ? "" : "s"} cannot make audio right now
          </h2>
          <ul className="ab-warnings">
            {down.map((r) => (
              <li key={`${r.hostUrl}|${r.voiceId}|${r.variant}`}>{r.line}</li>
            ))}
          </ul>
          <p className="ab-hint">
            This is the sentence <code>/health</code> cannot say. It answers <code>status: ok</code> and{" "}
            <code>model_loaded: true</code> for every pair above.
          </p>
        </section>
      ) : null}

      <section className="ab-panel">
        <div className="ab-shelf-controls">
          <h2 className="ab-h2">Readiness</h2>
          <button type="button" className="ab-btn ab-btn-sm" onClick={probeNow} disabled={busy}>
            {busy ? "Generating…" : "Check now"}
          </button>
        </div>
        <p className="ab-hint">
          Narration is <strong>{tts.mode}</strong> · narrator <code>{tts.voiceId}</code>{" "}
          {tts.narratorOk ? (
            <span className="ab-chip ab-chip-voice">a job would be accepted</span>
          ) : (
            <span className="ab-chip ab-chip-bad">a job would be refused right now</span>
          )}{" "}
          · checked every {tts.probeIntervalMin} min · {tts.listeners} listening
          {tts.medianMsPerChar === null ? "" : ` · ${tts.medianMsPerChar} ms per character over ${tts.paceSample} clips`}
        </p>
        {said !== null ? <p className="ab-hint-quiet">{said}</p> : null}
        {tts.probeDeferred !== null ? (
          <p className="ab-hint-quiet">
            Not probing: {tts.probeDeferred}. The rows below keep their real age rather than being overwritten with a guess.
          </p>
        ) : null}

        {tts.ready.length === 0 ? (
          <p className="ab-hint-quiet">
            Nothing has been probed yet. The first cycle runs {tts.probeIntervalMin} minutes after this server started — an empty table
            here means <em>we do not know</em>, which is a different claim from <em>everything is fine</em>.
          </p>
        ) : (
          <table className="ab-table">
            <thead>
              <tr>
                <th>Voice</th>
                <th>Variant</th>
                <th>State</th>
                <th>How we know</th>
                <th>Checked</th>
              </tr>
            </thead>
            <tbody>
              {tts.ready.map((r) => (
                <tr key={`${r.hostUrl}|${r.voiceId}|${r.variant}`} className={r.ok ? "is-positive" : "is-negative"}>
                  <td data-label="Voice">
                    <code>{r.voiceId}</code>
                  </td>
                  <td data-label="Variant">{r.variant}</td>
                  <td data-label="State">
                    {r.ok ? (
                      <span className="ab-chip ab-chip-voice">ok{r.latencyMs === null ? "" : ` ${(r.latencyMs / 1000).toFixed(1)}s`}</span>
                    ) : (
                      <>
                        <span className="ab-chip ab-chip-bad">DOWN</span>
                        {/* VERBATIM. `HTTP 500 from http://100.127.247.67:8095: Internal Server Error`
                            is the finding; a friendly rewrite is how one sentence comes to mean four
                            different failures. */}
                        <div className="ab-table-detail">{r.reason ?? "no reason recorded"}</div>
                      </>
                    )}
                  </td>
                  <td data-label="How we know">
                    {r.source === "probe" ? "generated 30 characters" : r.source === "clip" ? "a real narration" : "never asked the model"}
                  </td>
                  <td data-label="Checked">
                    {r.ageSec < 90 ? `${r.ageSec}s ago` : `${Math.round(r.ageSec / 60)} min ago`}
                    {r.stale ? <span className="ab-chip ab-chip-warn">stale</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="ab-panel">
        <h2 className="ab-h2">Hosts</h2>
        <table className="ab-table">
          <thead>
            <tr>
              <th>Host</th>
              <th>Reachable</th>
              <th>Serves</th>
              <th>Advertises</th>
            </tr>
          </thead>
          <tbody>
            {tts.hosts.map((h) => (
              <tr key={h.url} className={h.reachable && h.modelLoaded ? "is-positive" : "is-negative"}>
                <td data-label="Host">
                  <code>{h.url}</code>
                </td>
                <td data-label="Reachable">
                  {h.reachable ? (
                    <span className="ab-chip ab-chip-voice">{h.latencyMs === null ? "yes" : `${h.latencyMs} ms`}</span>
                  ) : (
                    <>
                      <span className="ab-chip ab-chip-bad">no</span>
                      <div className="ab-table-detail">{h.reason ?? "unreachable"}</div>
                    </>
                  )}
                  {h.reachable && !h.modelLoaded ? <span className="ab-chip ab-chip-warn">model not loaded</span> : null}
                </td>
                <td data-label="Serves">
                  {h.serves.length === 0 ? <span className="ab-chip ab-chip-bad">nothing</span> : h.serves.join(", ")}
                  {/* A voice this box is CONFIGURED to narrate in and the host has lost. §4.10: the
                      failure it produces is the whole library read in somebody else's voice, over a
                      host answering 200. */}
                  {h.missing.length > 0 ? (
                    <div className="ab-table-detail">
                      <span className="ab-chip ab-chip-bad">missing: {h.missing.join(", ")}</span>
                    </div>
                  ) : null}
                </td>
                <td data-label="Advertises">
                  {h.voices.join(", ") || "—"} · {h.variants.join(", ") || "—"}
                  {/* The seam P7c hangs on: a voice Karti clones shows up here the moment a host
                      actually serves it, without this screen pretending it is narratable yet. */}
                  {h.unmapped.length > 0 ? (
                    <div className="ab-table-detail">
                      {h.unmapped.length} voice{h.unmapped.length === 1 ? "" : "s"} on the box that <code>AB_TTS_HOSTS</code> does not map:{" "}
                      {h.unmapped.join(", ")}
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {tts.flags.length > 0 ? (
        <section className="ab-panel">
          <h2 className="ab-h2">Switches somebody threw</h2>
          {/* A drained lane looks exactly like a dead worker from every other screen, so every set flag
              is reported with who and when. A flag nobody is reminded of is a week of "why is
              narration broken". */}
          <ul className="ab-warnings">
            {tts.flags.map((f) => (
              <li key={f.key}>
                <code>{f.key}</code> = <code>{f.value}</code> · set by {f.setBy ?? "someone"}{" "}
                {f.ageSec < 90 ? `${f.ageSec}s` : `${Math.round(f.ageSec / 60)} min`} ago
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}
